export type Category = 'vetements' | 'epi' | 'batteries';
export type Sector = 'industrie' | 'medical' | 'restauration' | 'artisanat' | 'chantier';

export type SubcategoryVetements =
  | 'combinaisons' | 'vestes' | 'pantalons' | 'tuniques'
  | 'blouses' | 'tabliers' | 'tshirts' | 'polaires';

export type SubcategoryEpi =
  | 'tete' | 'mains' | 'pieds' | 'yeux'
  | 'respiration' | 'audition' | 'corps' | 'harnais';

export type SubcategoryBatteries =
  | 'defibrillateurs' | 'moniteurs' | 'respirateurs' | 'ecg'
  | 'ups-onduleurs' | 'baes' | 'alarmes' | 'outillage';

export type Subcategory = SubcategoryVetements | SubcategoryEpi | SubcategoryBatteries;

export interface Product {
  id: string;
  category: Category;
  subcategory: Subcategory;
  sector: Sector[];
  name: string;
  nameEn: string;
  brand: string;
  ref: string;
  price: number;
  currency: string;
  stock: number;
  moq: number;
  quoteThreshold: number;
  norm: string[];
  description: string;
  descriptionEn: string;
  images: string[];
  variants: { sizes?: string[]; colors?: string[] };
  features: string[];
  featuresEn: string[];
  bestseller?: boolean;
  badge?: string;
  badgeEn?: string;
}

export interface ProductListResponse {
  products: Product[];
  total: number;
}

export const SUBCATEGORY_LABELS: Record<Subcategory, { fr: string; en: string }> = {
  combinaisons:     { fr: 'Combinaisons',        en: 'Coveralls' },
  vestes:           { fr: 'Vestes & blousons',   en: 'Jackets' },
  pantalons:        { fr: 'Pantalons',            en: 'Trousers' },
  tuniques:         { fr: 'Tuniques médicales',  en: 'Medical tunics' },
  blouses:          { fr: 'Blouses & tabliers',  en: 'Lab coats & aprons' },
  tabliers:         { fr: 'Tabliers',            en: 'Aprons' },
  tshirts:          { fr: 'T-shirts & polos',    en: 'T-shirts & polos' },
  polaires:         { fr: 'Polaires & sweatshirts', en: 'Fleeces & sweatshirts' },
  tete:             { fr: 'Protection tête',     en: 'Head protection' },
  mains:            { fr: 'Protection mains',    en: 'Hand protection' },
  pieds:            { fr: 'Protection pieds',    en: 'Foot protection' },
  yeux:             { fr: 'Protection yeux',     en: 'Eye protection' },
  respiration:      { fr: 'Protection respiratoire', en: 'Respiratory protection' },
  audition:         { fr: 'Protection auditive', en: 'Hearing protection' },
  corps:            { fr: 'Protection du corps', en: 'Body protection' },
  harnais:          { fr: 'Harnais & travail en hauteur', en: 'Harness & working at height' },
  defibrillateurs:  { fr: 'Défibrillateurs (DAE)', en: 'Defibrillators (AED)' },
  moniteurs:        { fr: 'Moniteurs patients',  en: 'Patient monitors' },
  respirateurs:     { fr: 'Respirateurs / ventilateurs', en: 'Ventilators' },
  ecg:              { fr: 'ECG / électrocardiographes', en: 'ECG machines' },
  'ups-onduleurs':  { fr: 'UPS / Onduleurs',     en: 'UPS / Inverters' },
  baes:             { fr: 'BAES / Éclairage sécurité', en: 'Emergency lighting' },
  alarmes:          { fr: 'Alarmes & détection', en: 'Alarms & detection' },
  outillage:        { fr: 'Outillage électroportatif', en: 'Power tools' },
};

export const SECTOR_LABELS: Record<Sector, { fr: string; en: string }> = {
  industrie:   { fr: 'Industrie',    en: 'Industry' },
  medical:     { fr: 'Médical',      en: 'Medical' },
  restauration:{ fr: 'Restauration', en: 'Catering' },
  artisanat:   { fr: 'Artisanat',    en: 'Crafts' },
  chantier:    { fr: 'BTP / Chantier', en: 'Construction' },
};

export const getCategoryLabel = (cat: Category, lang: 'fr' | 'en') => {
  const labels: Record<Category, { fr: string; en: string }> = {
    vetements: { fr: 'Vêtements Pro', en: 'Workwear' },
    epi: { fr: 'EPI & Sécurité', en: 'PPE & Safety' },
    batteries: { fr: 'Batteries Pro', en: 'Pro Batteries' },
  };
  return labels[cat][lang];
};

export const QUOTE_INFO = {
  vetements: { threshold: 10, label: '10 unités', labelEn: '10 units' },
  epi: { threshold: 20, label: '20 unités', labelEn: '20 units' },
  batteries: { threshold: 5, label: '5 unités', labelEn: '5 units' },
};

export const SUBCATEGORIES_BY_CATEGORY: Record<Category, Subcategory[]> = {
  vetements: ['combinaisons','vestes','pantalons','tuniques','blouses','tabliers','tshirts','polaires'],
  epi:       ['tete','mains','pieds','yeux','respiration','audition','corps','harnais'],
  batteries: ['defibrillateurs','moniteurs','respirateurs','ecg','ups-onduleurs','baes','alarmes','outillage'],
};

export const BRANDS = ['ProWork', 'MediPro', 'ChefLine', 'SafeGuard', 'SecureStep', 'AirShield', 'MedPower', 'PowerCell'];
